// Fleet.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'; // Added useRef
import ViewOnlyNotice from './ViewOnlyNotice';
import axios from 'axios';
import Papa from 'papaparse';
import { useUser } from '../context/UserContext'; // Corrected import path
import { useNotification } from '../context/NotificationContext';
import {
  Typography, Box, Button, Paper, Divider, List, ListItem, ListItemText, ListItemSecondaryAction, Switch,
  Dialog, DialogTitle, DialogContent, TextField, DialogActions, FormControl, InputLabel, Select, MenuItem,
  IconButton, useMediaQuery, Grid, CircularProgress, Autocomplete, DialogContentText, TablePagination // Added DialogContentText and TablePagination
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
// Removed GoogleMapReact import
// Import Leaflet
import 'leaflet/dist/leaflet.css'; // Import Leaflet CSS
import { initializeMap, addMarker, removeMarker, fixDefaultIconPaths, updateMarkerPosition } from '../utils/mapUtils'; // Import Leaflet utils
import { useLoading } from '../context/LoadingContext';

// Removed GoogleMapReact specific MapMarker component

// Helper function to parse complex model strings
const parseModels = (modelString) => {
    if (!modelString) return [];
    // Split by comma or slash, trim whitespace, filter empty strings
    return modelString.split(/[,/]+/).map(m => m.trim()).filter(Boolean);
};

// Helper function to parse year strings (extracted and refined)
const parseYearString = (yearString) => {
    let options = [];
    let isRange = false;
    let yearValue = '';
    let extractedYears = new Set();

    if (yearString) {
        const yearRegex = /\b(\d{4})\b/g;
        const segments = yearString.split(',');

        segments.forEach(segment => {
            const segmentTrimmed = segment.trim();
            if (!segmentTrimmed) return;

            const foundYears = [];
            let match;
            while ((match = yearRegex.exec(segmentTrimmed)) !== null) {
                foundYears.push(parseInt(match[1], 10));
            }
            foundYears.sort((a, b) => a - b);

            if (foundYears.length > 0) {
                if (segmentTrimmed.includes('-') && foundYears.length >= 2) {
                    const startYear = foundYears[0];
                    const endYear = foundYears[foundYears.length - 1];
                    if (startYear <= endYear) {
                        for (let y = startYear; y <= endYear; y++) {
                            extractedYears.add(y);
                        }
                    } else {
                        foundYears.forEach(y => extractedYears.add(y));
                    }
                } else {
                    foundYears.forEach(y => extractedYears.add(y));
                }
            }
        });
    }

    options = Array.from(extractedYears).sort((a, b) => a - b);
    isRange = options.length > 1;
    yearValue = !isRange && options.length === 1 ? options[0].toString() : '';

    return { options, isRange, yearValue };
};


function Fleet({ vehicles, onSearch, page, rowsPerPage, totalCount, onPaginationChange, setVehicles /* TODO: Remove setVehicles when parent manages state */, readOnly = false }) {
  const { currentUser: userProfile } = useUser(); // Use useUser hook, alias currentUser
  const { showNotification } = useNotification();

  // Dialog states
  const [openAddVehicleDialog, setOpenAddVehicleDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false); // State for delete confirm dialog
  const [vehicleToDelete, setVehicleToDelete] = useState(null); // State for vehicle to delete

  // Form states
  const [newVehicle, setNewVehicle] = useState({ name: '', make: '', model: '', year: '', type: '', driverId: '', driverName: '', vendorId: '' });
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [editVehicleData, setEditVehicleData] = useState(null);

  // Data states
  const [csvVehicles, setCsvVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  // NEW state for Make/Model dropdowns
  const [makeOptions, setMakeOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  // State for year handling remains
  const [yearOptions, setYearOptions] = useState([]);
  const [isYearRange, setIsYearRange] = useState(false);

  // Loading/Error states
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [savingVehicle, setSavingVehicle] = useState(false); // Used for Add, Edit, Delete loading state
  const [error, setError] = useState(null);

  // Map states for Leaflet
  // Removed mapKey, mapLoading, mapError states for Google Maps
  const fleetMapContainerRef = useRef(null); // Ref for fleet map container
  const fleetMapRef = useRef(null); // Ref for fleet map instance
  const vehicleMarkersRef = useRef({}); // Ref to store vehicle markers { vehicleId: markerInstance }
  const [fleetMapInitialized, setFleetMapInitialized] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({ vendorFilter: '', companyFilter: '', facilityFilter: '' });
  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const { startLoading: startGlobalLoading, stopLoading: stopGlobalLoading } = useLoading();

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, []);

  // Determine user capabilities
  const canUseAdvancedFilters = userProfile && (['OW', 'sOW', 'OWp', 'RM'].includes(userProfile.primaryRole) || (userProfile.primaryRole === 'N/A' && userProfile.secondaryRoles?.admin));

  // Create enriched vehicles list with driver names populated
  const enrichedVehicles = useMemo(() => {
    if (!vehicles || !Array.isArray(vehicles)) return [];
    
    return vehicles.map(vehicle => {
      // If vehicle already has driverName, use it
      if (vehicle.driverName || vehicle.driver) {
        return vehicle;
      }
      
      // If vehicle has driverId, find the corresponding driver
      if (vehicle.driverId && drivers.length > 0) {
        const driver = drivers.find(d => (d._id || d.id) === vehicle.driverId);
        if (driver) {
          return {
            ...vehicle,
            driverName: `${driver.firstName || ''} ${driver.lastName || ''}`.trim()
          };
        }
      }
      
      // Return vehicle as-is if no driver found
      return vehicle;
    });
  }, [vehicles, drivers]);

  // Initial data fetching
  useEffect(() => {
    const headers = getAuthHeader();
    if (!headers) return;

    fetchCsvVehicles();
    fetchDrivers(headers);
    // Removed fetchMapKey(headers);

    if (canUseAdvancedFilters) {
      fetchFilterData(headers);
    }
  }, [getAuthHeader, userProfile, canUseAdvancedFilters]);

  // NEW: Populate Make options when CSV data is loaded
  useEffect(() => {
    if (csvVehicles.length > 0) {
      const makes = [...new Set(csvVehicles.map(v => v.Make?.trim()).filter(Boolean))].sort();
      setMakeOptions(makes);
      // console.log("Make options populated:", makes); // Debug log // Removed log
    }
  }, [csvVehicles]);


  // Removed fetchMapKey function

  const fetchCsvVehicles = async () => {
     setLoadingCsv(true);
     setError(null);
     try {
      const response = await fetch('/csvs/VehicleList.csv');
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const csvText = await response.text();
      Papa.parse(csvText, { header: true, skipEmptyLines: true, complete: (results) => {
          const validVehicles = results.data.filter(v => v.Make?.trim()); // Only need Make to be present initially
          // Store raw valid vehicles, make/model parsing happens later
          setCsvVehicles(validVehicles);
          // console.log("CSV vehicles loaded:", validVehicles.length); // Debug log // Removed log
      }, error: (err) => {
        const errorMessage = 'Failed to load vehicle database.';
        showNotification(errorMessage, 'error');
        setError(errorMessage);
      } });
    } catch (err) {
      const errorMessage = 'Failed to load vehicle database.';
      showNotification(errorMessage, 'error');
      setError(errorMessage);
    }
    finally { setLoadingCsv(false); }
  };

  const fetchDrivers = async (headers) => {
    setLoadingDrivers(true);
    setError(null);
    if (!headers) { setError('Not authenticated.'); setLoadingDrivers(false); return; }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`, { headers });
      
      // Get current user's vendor identifier
      const currentUserVendorId = userProfile?.vendorId || userProfile?.vendorNumber;
      
      // Handle different response structures
      let usersData = [];
      if (Array.isArray(response.data)) {
        usersData = response.data;
      } else if (response.data && Array.isArray(response.data.records)) {
        usersData = response.data.records;
      } else if (response.data && Array.isArray(response.data.users)) {
        usersData = response.data.users;
      } else if (response.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      } else {
        console.error('Unexpected API response structure:', response.data);
        const errorMessage = 'Unexpected data format from server.';
        showNotification(errorMessage, 'error');
        setError(errorMessage);
        return;
      }
      
      // Filter for active drivers that belong to the same vendor as the current user
      const driversOnly = usersData.filter(u => {
        const isActiveDriver = u.isActive !== false && (
          u.primaryRole === 'Driver' ||
          u.secondaryRoles?.driver === true ||
          u.isDriver === true
        );
        
        if (!isActiveDriver) return false;
        
        // Universal vendor filtering: Both Org 1 and Org 2 users should only see drivers from their vendor
        if (currentUserVendorId) {
          const driverVendorId = u.vendorId || u.vendorNumber;
          return driverVendorId === currentUserVendorId;
        }
        
        // If current user has no vendor identifier, show no drivers (empty dropdown)
        return false;
      });
      
      setDrivers(driversOnly);
    } catch (err) {
        // console.error('Error fetching drivers:', err); // Removed log
        const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load driver data.';
        showNotification(errorMessage, 'error');
        setError(errorMessage);
    } finally {
        setLoadingDrivers(false);
    }
  };

  const fetchFilterData = async (headers) => {
    setLoadingFilters(true);
    setError(null);
    try {
      const [vendorRes, companyRes, facilityRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/users/vendor-list`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/users/distinct/companies`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/facilities`, { headers })
      ]);
      setVendors(vendorRes.data || []);
      setCompanies(companyRes.data || []);
      
      // Handle different response structures for facilities
      let facilitiesData = [];
      if (Array.isArray(facilityRes.data)) {
        facilitiesData = facilityRes.data;
      } else if (facilityRes.data && Array.isArray(facilityRes.data.facilities)) {
        facilitiesData = facilityRes.data.facilities;
      } else if (facilityRes.data && Array.isArray(facilityRes.data.data)) {
        facilitiesData = facilityRes.data.data;
      } else if (facilityRes.data && Array.isArray(facilityRes.data.records)) {
        facilitiesData = facilityRes.data.records;
      } else {
        console.error('Unexpected facilities API response structure:', facilityRes.data);
        facilitiesData = [];
      }
      
      const distinctFacilities = [...new Set(facilitiesData.map(f => f.facilityName).filter(Boolean))];
      setFacilities(distinctFacilities);
    } catch (error) {
      // console.error("Error fetching filter data for Fleet tab:", error); // Removed log
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to load filter options.';
      showNotification(errorMessage, 'error');
      setError(errorMessage);
    } finally {
      setLoadingFilters(false);
    }
  };

  // --- Filter Handling ---
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (onSearch && canUseAdvancedFilters) {
      const timerId = setTimeout(() => {
          const activeFilters = Object.entries(filters).reduce((acc, [key, value]) => {
              if (value) acc[key] = value;
              return acc;
          }, {});
          onSearch(activeFilters);
      }, 500);
      return () => clearTimeout(timerId);
    }
     else if (!canUseAdvancedFilters && (filters.vendorFilter || filters.companyFilter || filters.facilityFilter)) {
         setFilters({ vendorFilter: '', companyFilter: '', facilityFilter: '' });
         if(onSearch) onSearch({});
     }
  }, [filters, onSearch, canUseAdvancedFilters]);

  // --- Add/Edit Dialog Logic ---

   const handleOpenAddVehicleDialog = () => {
       // Reset state for the new vehicle form
       setNewVehicle({ name: '', make: '', model: '', year: '', type: '', driverId: '', driverName: '', vendorId: userProfile?.vendorNumber || userProfile?.vendorId || '' });
       setModelOptions([]); // Clear model options
       setYearOptions([]); // Clear year options
       setIsYearRange(false); // Reset year range flag
       setError(null);
       
       
       setOpenAddVehicleDialog(true);
   };

   const handleCloseAddVehicleDialog = () => {
       setOpenAddVehicleDialog(false);
   };

   // --- Delete Vehicle Handlers ---
   const handleOpenDeleteConfirm = (vehicle) => {
     setVehicleToDelete(vehicle); // Store vehicle info for the dialog
     setDeleteConfirmOpen(true);
   };

   const handleCloseDeleteConfirm = () => {
     setDeleteConfirmOpen(false);
     setVehicleToDelete(null);
     setError(null); // Clear any previous delete errors
   };

   const handleConfirmDelete = async () => {
     if (!vehicleToDelete || !vehicleToDelete._id) return;

     setSavingVehicle(true); // Reuse saving state for loading indicator
     setError(null);
     const headers = getAuthHeader();
     if (!headers) {
       const errorMessage = "Authentication error.";
       showNotification(errorMessage, 'error');
       setError(errorMessage);
       setSavingVehicle(false);
       // Keep dialog open to show error
       return;
     }

     try {
       await axios.delete(`${process.env.REACT_APP_API_URL}/api/vehicles/${vehicleToDelete._id}`, { headers });
       // Update frontend state by removing the vehicle
       if (setVehicles) {
           setVehicles(prev => prev.filter(v => v._id !== vehicleToDelete._id));
       }
       showNotification('Vehicle deleted successfully', 'success');
       handleCloseDeleteConfirm(); // Close dialog on success
     } catch (err) {
       // console.error("Error deleting vehicle:", err); // Removed log
       const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to delete vehicle.';
       showNotification(errorMessage, 'error');
       setError(errorMessage);
       // Keep dialog open to show error
     } finally {
       setSavingVehicle(false);
     }
   };
   // --- End Delete Vehicle Handlers ---

   // NEW Handler for Make selection
   const handleMakeChange = (event, selectedMake) => {
       // console.log("Make selected:", selectedMake); // Removed log
       // Reset dependent fields
       setNewVehicle(prev => ({ ...prev, make: selectedMake || '', model: '', year: '' }));
       setModelOptions([]);
       setYearOptions([]);
       setIsYearRange(false);

       if (selectedMake) {
           const modelsForMake = new Set();
           csvVehicles
               .filter(v => v.Make?.trim() === selectedMake)
               .forEach(v => {
                   const parsed = parseModels(v.Model?.trim());
                   parsed.forEach(model => modelsForMake.add(model));
               });
           const sortedModels = Array.from(modelsForMake).sort();
           setModelOptions(sortedModels);
           // console.log("Model options populated:", sortedModels); // Debug log // Removed log
       }
   };

   // NEW Handler for Model selection
   const handleModelChange = (event, selectedModel) => {
       // console.log("Model selected:", selectedModel); // Removed log
       // Reset year
       setNewVehicle(prev => ({ ...prev, model: selectedModel || '', year: '' }));
       setYearOptions([]);
       setIsYearRange(false);

       if (selectedModel && newVehicle.make) {
           // Find the specific CSV entry to get the year string
           // This assumes the combination of Make and the *parsed* Model is unique enough,
           // or we take the first match. Might need refinement if multiple entries share Make/ParsedModel but have different year strings.
           const vehicleEntry = csvVehicles.find(v =>
               v.Make?.trim() === newVehicle.make &&
               parseModels(v.Model?.trim()).includes(selectedModel)
           );

           if (vehicleEntry) {
               const yearString = (vehicleEntry['Production years'] || vehicleEntry['Model Years (US/Canada)'] || '').trim();
               // console.log(`Found yearString for ${newVehicle.make} ${selectedModel}: "${yearString}"`); // Removed log
               const { options, isRange, yearValue } = parseYearString(yearString);
               setYearOptions(options);
               setIsYearRange(isRange);
               setNewVehicle(prev => ({ ...prev, year: yearValue })); // Set single year if applicable
               // console.log('Year state updated - isRange:', isRange, 'yearValue:', yearValue, 'options:', options); // Removed log
           } else {
               // console.log(`Could not find matching entry for ${newVehicle.make} ${selectedModel} to get year string.`); // Removed log
           }
       }
   };


   // Handler for other form fields (Name, Type, Year if manual/single)
   const handleNewVehicleInputChange = (event) => {
       const { name, value } = event.target;
       setNewVehicle(prev => ({ ...prev, [name]: value }));
   };


   // Handler for Driver Autocomplete selection in ADD dialog
   const handleAddDriverAutocompleteChange = (event, newValue) => {
       setNewVehicle(prev => ({
           ...prev,
           driverId: newValue ? (newValue._id || newValue.id) : '',
           driverName: newValue ? `${newValue.firstName || ''} ${newValue.lastName || ''}`.trim() : ''
       }));
   };

    // Handler for Driver Autocomplete selection in EDIT dialog
   const handleEditDriverAutocompleteChange = (event, newValue) => {
       setEditVehicleData(prev => ({
           ...prev,
           driverId: newValue ? (newValue._id || newValue.id) : '',
           // Note: We might want to update driverName here too if needed in the edit view
       }));
   };


   const handleSaveNewVehicle = async () => {
       setSavingVehicle(true);
       setError(null);
       const headers = getAuthHeader();
       if (!headers) { setError("Authentication error."); setSavingVehicle(false); return; }
       
       // Validation: Ensure Make, Model, Year, Type, and Name are filled
       if (!newVehicle.name || !newVehicle.type || !newVehicle.year || !newVehicle.make || !newVehicle.model) {
           setError("Vehicle Name, Make, Model, Type, and Year are required.");
           setSavingVehicle(false);
           return;
       }

       // Ensure we have a vendorId from the current user profile
       const vendorId = userProfile?.vendorId || userProfile?.vendorNumber;
       if (!vendorId) {
           console.error('No vendorId found in current user profile:', userProfile);
           const errorMessage = 'Unable to save vehicle: Missing vendor information. Please refresh and try again.';
           showNotification(errorMessage, 'error');
           setError(errorMessage);
           setSavingVehicle(false);
           return;
       }

       const vehicleToSave = {
           name: newVehicle.name,
           make: newVehicle.make,
           model: newVehicle.model,
           year: parseInt(newVehicle.year),
           type: newVehicle.type,
           driverId: newVehicle.driverId || null,
           vendorId: vendorId,
           status: 'Off Duty'
       };

       console.log('Saving new vehicle with data:', vehicleToSave);

       try {
           const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/vehicles`, vehicleToSave, { headers });
           if (setVehicles) {
               setVehicles(prev => [...prev, response.data]);
           }
           showNotification('Vehicle added successfully', 'success');
           handleCloseAddVehicleDialog();
       } catch (err) {
           console.error("Error saving new vehicle:", err);
           if (err.response) {
               console.error('Response data:', err.response.data);
               console.error('Response status:', err.response.status);
               console.error('Request payload that failed:', vehicleToSave);
           }
           const errorMessage = err.response?.data?.message || err.response?.data?.errors?.join(', ') || err.response?.data?.error || err.message || 'Failed to save vehicle.';
           showNotification(errorMessage, 'error');
           setError(errorMessage);
       } finally {
           setSavingVehicle(false);
       }
   };

   const handleEditVehicle = (vehicle) => {
       setEditingVehicleId(vehicle._id);
       setEditVehicleData({
           ...vehicle,
           driverId: vehicle.driverId || vehicle.driver || '',
           status: vehicle.status || 'Off Duty'
       });
       setError(null);
       setOpenEditDialog(true);
   };
   const handleCloseEditDialog = () => {
       setOpenEditDialog(false);
       setEditingVehicleId(null);
       setEditVehicleData(null);
   };

   const handleEditFormChange = (field, value) => {
       setEditVehicleData(prev => ({ ...prev, [field]: value }));
   };

   const handleSaveEditedVehicle = async () => {
       if (!editVehicleData || !editVehicleData._id) return;
       setSavingVehicle(true);
       setError(null);
       const headers = getAuthHeader();
       if (!headers) {
           const errorMessage = "Authentication error.";
           showNotification(errorMessage, 'error');
           setError(errorMessage);
           setSavingVehicle(false);
           return;
       }
       if (!editVehicleData.name || !editVehicleData.type || !editVehicleData.year || !editVehicleData.make || !editVehicleData.model) {
           const errorMessage = "Vehicle Name, Make, Model, Type, and Year are required.";
           showNotification(errorMessage, 'error');
           setError(errorMessage);
           setSavingVehicle(false);
           return;
       }

       try {
           const { _id, ...updateData } = editVehicleData;
           const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/vehicles/${_id}`, updateData, { headers });
           if (setVehicles) {
               setVehicles(prev => prev.map(v => v._id === _id ? response.data : v));
           }
           showNotification('Vehicle updated successfully', 'success');
           handleCloseEditDialog();
       } catch (err) {
           // console.error("Error saving edited vehicle:", err); // Removed log
           const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save changes.';
           showNotification(errorMessage, 'error');
           setError(errorMessage);
       } finally {
           setSavingVehicle(false);
       }
   };

   const handleToggleStatus = async (vehicle) => {
       if (!vehicle || !vehicle._id) return;
       setSavingVehicle(true);
       setError(null);
       const headers = getAuthHeader();
       if (!headers) {
           const errorMessage = "Authentication error.";
           showNotification(errorMessage, 'error');
           setError(errorMessage);
           setSavingVehicle(false);
           return;
       }

       const newStatus = vehicle.status === 'On Duty' ? 'Off Duty' : 'On Duty';

       try {
           const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/vehicles/${vehicle._id}`, { status: newStatus }, { headers });
           if (setVehicles) {
               setVehicles(prev => prev.map(v => v._id === vehicle._id ? response.data : v));
           }
           showNotification(`Vehicle status updated to ${newStatus}`, 'success');
       } catch (err) {
           // console.error("Error toggling vehicle status:", err); // Removed log
           const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to update status.';
           showNotification(errorMessage, 'error');
           setError(errorMessage);
       } finally {
           setSavingVehicle(false);
       }
   };

  // Effect to initialize the fleet map
  useEffect(() => {
    if (fleetMapContainerRef.current && !fleetMapRef.current) {
      fixDefaultIconPaths();
      const mapInstance = initializeMap(fleetMapContainerRef.current, {
        center: [39.8283, -98.5795], // Default US center
        zoom: 4,
      });
      fleetMapRef.current = mapInstance;
      setFleetMapInitialized(true);
    }
    // Cleanup
    return () => {
      if (fleetMapRef.current) {
        fleetMapRef.current.remove();
        fleetMapRef.current = null;
        setFleetMapInitialized(false);
        vehicleMarkersRef.current = {}; // Clear markers on unmount
      }
    };
  }, []); // Run only once on mount

  // Effect to update vehicle markers when vehicles data or map initializes
  useEffect(() => {
    if (fleetMapInitialized && fleetMapRef.current) {
      const currentMap = fleetMapRef.current;
      const currentMarkers = vehicleMarkersRef.current;
      const newMarkers = {};

      enrichedVehicles.forEach(vehicle => {
        const hasCoords = vehicle.lat && vehicle.lng && typeof vehicle.lat === 'number' && typeof vehicle.lng === 'number';
        if (hasCoords) {
          const coords = [vehicle.lat, vehicle.lng];
          const markerId = vehicle._id;
          const popupContent = `<b>${vehicle.name}</b><br>Status: ${vehicle.status || 'Unknown'}<br>Driver: ${vehicle.driverName || vehicle.driver || 'N/A'}`;

          if (currentMarkers[markerId]) {
            // Update existing marker
            updateMarkerPosition(currentMarkers[markerId], coords);
            // Update popup content if needed (Leaflet doesn't have a direct update method, might need to rebind)
            currentMarkers[markerId].unbindPopup().bindPopup(popupContent);
            newMarkers[markerId] = currentMarkers[markerId]; // Keep the reference
            delete currentMarkers[markerId]; // Remove from old list
          } else {
            // Add new marker
            // TODO: Use different icons based on status (On Duty vs Off Duty)
            const newMarker = addMarker(currentMap, coords, {}, popupContent);
            newMarkers[markerId] = newMarker;
          }
        }
      });

      // Remove markers for vehicles that are no longer present
      Object.values(currentMarkers).forEach(oldMarker => {
        removeMarker(currentMap, oldMarker);
      });

      // Update the markers ref
      vehicleMarkersRef.current = newMarkers;

      // Optionally fit bounds to markers if there are any
      // const markerInstances = Object.values(newMarkers);
      // if (markerInstances.length > 0) {
      //   const bounds = L.featureGroup(markerInstances).getBounds();
      //   if (bounds.isValid()) {
      //     currentMap.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
      //   }
      // } else {
      //    setMapView(currentMap, [39.8283, -98.5795], 4); // Reset view if no markers
      // }

    }
  }, [enrichedVehicles, fleetMapInitialized]); // Depend on enriched vehicles list and map status


  return (
    <Box mt={2}>
       {/* Search/Filter Section (remains the same) */}
       {canUseAdvancedFilters && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>Filter Fleet</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}> <FormControl fullWidth size="small" disabled={loadingFilters}> <InputLabel>Vendor</InputLabel> <Select name="vendorFilter" value={filters.vendorFilter} label="Vendor" onChange={handleFilterChange} > <MenuItem value=""><em>All Vendors</em></MenuItem> {vendors.map((v) => ( <MenuItem key={v.vendorIdentifier} value={v.vendorIdentifier}>{v.companyName} ({v.vendorIdentifier})</MenuItem> ))} </Select> </FormControl> </Grid>
              <Grid item xs={12} sm={4}> <FormControl fullWidth size="small" disabled={loadingFilters}> <InputLabel>Company</InputLabel> <Select name="companyFilter" value={filters.companyFilter} label="Company" onChange={handleFilterChange} > <MenuItem value=""><em>All Companies</em></MenuItem> {companies.map((c) => ( <MenuItem key={c} value={c}>{c}</MenuItem> ))} </Select> </FormControl> </Grid>
              <Grid item xs={12} sm={4}> <FormControl fullWidth size="small" disabled={loadingFilters}> <InputLabel>Facility</InputLabel> <Select name="facilityFilter" value={filters.facilityFilter} label="Facility" onChange={handleFilterChange} > <MenuItem value=""><em>All Facilities</em></MenuItem> {facilities.map((fName) => ( <MenuItem key={fName} value={fName}>{fName}</MenuItem> ))} </Select> </FormControl> </Grid>
            </Grid>
          </Box>
        )}

      {/* Add New Vehicle Button (remains the same) */}
      {readOnly && <ViewOnlyNotice />}
      {!readOnly && (
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddVehicleDialog}> Add New Vehicle </Button>
      </Box>
      )}

      {/* Main Content Area */}
      <Box display={isSmallScreen ? 'block' : 'flex'} height="500px">
        {/* Leaflet Map Panel */}
        <Box flex={1} mr={isSmallScreen ? 0 : 2} mb={isSmallScreen ? 2 : 0} sx={{ border: '1px solid #ccc', position: 'relative' }} ref={fleetMapContainerRef}>
           {/* Leaflet map initializes here */}
           {!fleetMapInitialized && (
             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
               <CircularProgress />
               <Typography sx={{ ml: 2 }}>Initializing Map...</Typography>
             </Box>
           )}
        </Box>
        {/* Vehicle List Panel */}
        <Box flex={1} ml={isSmallScreen ? 0 : 2} component={Paper} elevation={3} p={2} sx={{ overflowY: 'auto', maxHeight: '500px' }}>
          <Typography variant="h6">Vehicles</Typography>
          <Divider sx={{ my: 2 }} />
          <List>
            {enrichedVehicles.map((vehicle) => {
              const key = vehicle._id; // Use _id as the primary key
              return (
                <ListItem key={key}>
                  <ListItemText primary={vehicle.name} secondary={`Driver: ${vehicle.driverName || vehicle.driver || 'N/A'} | Type: ${vehicle.type}`} />
                  <ListItemSecondaryAction>
                    {!readOnly && (<>
                    <IconButton edge="end" aria-label="edit" onClick={() => handleEditVehicle(vehicle)} color="primary" sx={{ mr: 0.5 }} >
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleOpenDeleteConfirm(vehicle)} color="error" sx={{ mr: 0.5 }}>
                      <DeleteIcon />
                    </IconButton>
                    </>)}
                    <Switch
                      checked={vehicle.status === 'On Duty'}
                      onChange={() => handleToggleStatus(vehicle)}
                      color="primary"
                      inputProps={{ 'aria-label': 'controlled' }}
                      disabled={savingVehicle || readOnly}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
            );})}
          </List>
        </Box>
      </Box>

      {/* Pagination Controls */}
      <TablePagination
        component="div"
        count={totalCount || 0} // Use totalCount from props, default to 0
        page={page || 0} // Use page from props, default to 0
        rowsPerPage={rowsPerPage || 10} // Use rowsPerPage from props, default to 10
        onPageChange={(event, newPage) => onPaginationChange(newPage, rowsPerPage)}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          onPaginationChange(0, newRowsPerPage); // Reset to page 0 on rows per page change
        }}
        rowsPerPageOptions={[5, 10, 25, 50]} // Standard options
        sx={{ mt: 2, borderTop: '1px solid rgba(224, 224, 224, 1)' }} // Add some spacing and a top border
      />

      {/* Add Vehicle Dialog */}
       <Dialog open={openAddVehicleDialog} onClose={handleCloseAddVehicleDialog} maxWidth="sm" fullWidth >
        <DialogTitle>Add New Vehicle</DialogTitle>
        <DialogContent>
          {loadingCsv || loadingDrivers ? ( <CircularProgress /> ) : (
            <>
              {/* REMOVED old Autocomplete for full vehicle object */}
              {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}> <Box sx={{ flex: 1 }}><Divider /></Box> <Box sx={{ px: 2 }}><Typography variant="body2">or enter manually</Typography></Box> <Box sx={{ flex: 1 }}><Divider /></Box> </Box> */}

              <TextField
                margin="dense"
                label="Name *"
                name="name" // Add name attribute
                fullWidth
                required
                value={newVehicle.name}
                onChange={handleNewVehicleInputChange} // Use generic handler
              />
              <Autocomplete
                options={makeOptions}
                value={newVehicle.make}
                onChange={handleMakeChange} // Use new handler
                renderInput={(params) => (
                  <TextField {...params} label="Make *" margin="dense" fullWidth required />
                )}
                sx={{ mt: 1 }}
              />
              <Autocomplete
                options={modelOptions}
                value={newVehicle.model}
                disabled={!newVehicle.make || modelOptions.length === 0} // Disable until make selected
                onChange={handleModelChange} // Use new handler
                getOptionLabel={(option) => option} // Display the model string directly
                renderInput={(params) => (
                  <TextField {...params} label="Model *" margin="dense" fullWidth required />
                )}
                sx={{ mt: 1 }}
              />

              {/* Conditional Year Input */}
              {isYearRange ? (
                <FormControl fullWidth margin="dense" required disabled={!newVehicle.model}> {/* Disable until model selected */}
                  <InputLabel id="year-select-label">Year *</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={newVehicle.year}
                    label="Year *"
                    name="year" // Add name attribute
                    onChange={handleNewVehicleInputChange} // Use generic handler
                  >
                    <MenuItem value=""><em>- Select Year -</em></MenuItem>
                    {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  margin="dense"
                  label="Year *"
                  name="year" // Add name attribute
                  fullWidth
                  required
                  type="number"
                  value={newVehicle.year}
                  onChange={handleNewVehicleInputChange} // Use generic handler
                  disabled={!newVehicle.model && yearOptions.length === 0} // Disable unless single year determined or manually entered
                />
              )}
              <FormControl fullWidth margin="dense" required>
                <InputLabel id="type-select-label">Type *</InputLabel>
                <Select
                    labelId="type-select-label"
                    label="Type *"
                    name="type" // Add name attribute
                    value={newVehicle.type}
                    onChange={handleNewVehicleInputChange} // Use generic handler
                >
                    <MenuItem value="">-Select-</MenuItem>
                    <MenuItem value="Flatbed">Flatbed</MenuItem>
                    <MenuItem value="Wheel Lift">Wheel Lift</MenuItem>
                    <MenuItem value="Light Duty">Light Duty</MenuItem>
                    <MenuItem value="Medium Duty">Medium Duty</MenuItem>
                    <MenuItem value="Heavy Duty">Heavy Duty</MenuItem>
                    <MenuItem value="Super Heavy">Super Heavy</MenuItem>
                    <MenuItem value="Service Truck">Service Truck</MenuItem>
                    <MenuItem value="Covered Flatbed">Covered Flatbed</MenuItem>
                    <MenuItem value="Flatbed + Straps">Flatbed + Straps</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              <Autocomplete
                options={drivers}
                getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''}`.trim()}
                value={drivers.find(d => (d._id || d.id) === newVehicle.driverId) || null}
                onChange={handleAddDriverAutocompleteChange}
                isOptionEqualToValue={(option, value) => (option._id || option.id) === (value?._id || value?.id)}
                renderInput={(params) => (
                  <TextField {...params} label="Assign Driver (optional)" helperText="Leave empty for a shared truck any driver can take per job" margin="dense" fullWidth />
                )}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
            <Button onClick={handleCloseAddVehicleDialog} disabled={savingVehicle}>Cancel</Button>
            <Button onClick={handleSaveNewVehicle} color="primary" variant="contained" disabled={savingVehicle || !newVehicle.name || !newVehicle.make || !newVehicle.model || !newVehicle.type || !newVehicle.year}> {/* Updated validation */}
                {savingVehicle ? <CircularProgress size={24} /> : 'Save'}
            </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Vehicle Dialog */}
       <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth >
        <DialogTitle> Edit Vehicle {editVehicleData?.name ? ` - ${editVehicleData.name}` : ''} </DialogTitle>
        <DialogContent>
          {!editVehicleData ? ( <CircularProgress /> ) : (
            <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold">Vehicle Information</Typography></Grid>
                <Grid item xs={12} md={6}><TextField fullWidth label="Name *" value={editVehicleData.name || ''} onChange={(e) => handleEditFormChange('name', e.target.value)} required /></Grid>
                <Grid item xs={12} md={6}> <FormControl fullWidth required><InputLabel id="edit-type-label">Type *</InputLabel> <Select labelId="edit-type-label" label="Type *" value={editVehicleData.type || ''} onChange={(e) => handleEditFormChange('type', e.target.value)} > <MenuItem value="">-Select-</MenuItem> <MenuItem value="Flatbed">Flatbed</MenuItem> <MenuItem value="Wheel Lift">Wheel Lift</MenuItem> <MenuItem value="Light Duty">Light Duty</MenuItem> <MenuItem value="Medium Duty">Medium Duty</MenuItem> <MenuItem value="Heavy Duty">Heavy Duty</MenuItem> <MenuItem value="Super Heavy">Super Heavy</MenuItem> <MenuItem value="Service Truck">Service Truck</MenuItem> <MenuItem value="Covered Flatbed">Covered Flatbed</MenuItem> <MenuItem value="Flatbed + Straps">Flatbed + Straps</MenuItem> <MenuItem value="Other">Other</MenuItem> </Select> </FormControl> </Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Make" value={editVehicleData.make || ''} onChange={(e) => handleEditFormChange('make', e.target.value)} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Model" value={editVehicleData.model || ''} onChange={(e) => handleEditFormChange('model', e.target.value)} /></Grid>
                <Grid item xs={12} md={4}><TextField fullWidth label="Year *" type="number" value={editVehicleData.year || ''} onChange={(e) => handleEditFormChange('year', e.target.value)} required /></Grid>
                <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Driver Assignment</Typography></Grid>
                <Grid item xs={12}>
                   <Autocomplete
                     options={drivers}
                     getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''}`.trim()}
                     value={drivers.find(d => (d._id || d.id) === editVehicleData.driverId) || null}
                     onChange={handleEditDriverAutocompleteChange}
                     isOptionEqualToValue={(option, value) => (option._id || option.id) === (value?._id || value?.id)}
                     renderInput={(params) => (
                       <TextField {...params} label="Assign Driver (optional)" helperText="Leave empty for a shared truck any driver can take per job" fullWidth />
                     )}
                   />
                </Grid>
                <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Vehicle Status</Typography></Grid>
                <Grid item xs={12}> <FormControl fullWidth><InputLabel id="edit-status-label">Status</InputLabel> <Select labelId="edit-status-label" label="Status" value={editVehicleData.status || 'Off Duty'} onChange={(e) => handleEditFormChange('status', e.target.value)} > <MenuItem value="On Duty">On Duty</MenuItem> <MenuItem value="Off Duty">Off Duty</MenuItem> <MenuItem value="Maintenance">Maintenance</MenuItem> <MenuItem value="Out of Service">Out of Service</MenuItem> </Select> </FormControl> </Grid>
                <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Additional Details</Typography></Grid>
                <Grid item xs={12}><TextField fullWidth label="Notes" multiline rows={4} value={editVehicleData.notes || ''} onChange={(e) => handleEditFormChange('notes', e.target.value)} /></Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
         <DialogActions> <Button onClick={handleCloseEditDialog} disabled={savingVehicle}> Cancel </Button> <Button onClick={handleSaveEditedVehicle} variant="contained" color="primary" disabled={savingVehicle || !editVehicleData?.name || !editVehicleData?.type || !editVehicleData?.year}> {savingVehicle ? <CircularProgress size={24} /> : 'Save Changes'} </Button> </DialogActions>
       </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        aria-labelledby="delete-confirm-dialog-title"
        aria-describedby="delete-confirm-dialog-description"
      >
        <DialogTitle id="delete-confirm-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-confirm-dialog-description">
            Are you sure you want to delete the vehicle "{vehicleToDelete?.name || 'this vehicle'}"? This action cannot be undone.
          </DialogContentText>
          {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} disabled={savingVehicle}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={savingVehicle} autoFocus>
            {savingVehicle ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
     </Box>
   ); // This closes the return statement's main Box
 } // This closes the Fleet function
// Removed extra parenthesis that was here

export default Fleet;

<environment_details>
# VSCode Visible Files
src/settings/Fleet.js

# VSCode Open Tabs
src/components/SideMenu.js
backend/uploads/applications/rate-sheet-White-Knight-Roadside-Services-White-Knight-Roadside-Services.pdf
src/pages/Dashboard.js
src/components/JobsTable.js
src/components/JobDialogs/JobDialogManager.js
backend/routes/jobRoutes.js
backend/routes/spRoutes.js
src/components/JobDialogs/DispatchDialog.js
src/components/JobDialog.js
backend/controllers/driverController.js
backend/utils/rankingUtils.js
src/hooks/useJobData.js
src/hooks/useJobSocket.js
src/dashboard/Jobs.js
src/context/NotificationContext.js
src/hooks/useVehicleData.js
src/utils/authUtils.js
src/pages/Login.js
src/settings/Drivers.js
src/utils/axiosConfig.js
src/services/jobService.js
src/utils/jobUtils.js
src/utils/dashboardUtils.js
src/utils/jobActionHandlers.js
src/context/PermissionContext.js
src/context/UserContext.js
src/utils/objectUtils.js
src/App.js
src/components/ApplicationForm/appformcomp.js
src/utils/PermissionManager.js
src/utils/permissionStore.js
src/pages/Settings.js
src/settings/components/CreateUserDialog.js
src/utils/socket.js
backend/check-roland.js
backend/check-users.js
backend/download-fonts.js
backend/fix-owner-mongodb.js
backend/controllers/applicationsController.js
backend/config/db.js
backend/controllers/authController.js
backend/fix-owner-role.js
backend/migrate-application-backgroundCheck.js
backend/jobs/facilityStatusManager.js
backend/migrate-application-schedule.js
backend/middleware/authMiddleware.js
backend/migrate-background-check-path.js
backend/middleware/roleMiddleware.js
backend/migrate-facilities-rates.js
backend/controllers/facilityController.js
backend/controllers/jobController.js
backend/controllers/spController.js
backend/controllers/job/acceptance.js
backend/controllers/locationRequestController.js
backend/migrate-facility-status.js
backend/controllers/job/assignment.js
backend/controllers/job/autoRejection.js
backend/migrate-goa-rate.js
backend/controllers/rateController.js
src/services/jobDashboardService.js
src/settings/Fleet.js

# Current Time
4/6/2025, 10:36:11 AM (America/Chicago, UTC-5:00)

# Current Mode
ACT MODE
</environment_details>
